import {
  STITCH_TYPE_TARGETS,
  LAYERS_INTRO_STEP,
  AUTO_FILL_BASELINE_STEP,
  CLOSE_STITCH_PANEL_STEP,
  SHOW_ALL_LAYERS_STEP,
  createExportSteps,
} from '../shared/steps';
import { layers } from './fixture';

// The canvas is always on screen, so it can't tell the tutorial whether a click
// would land. These controls exist only while the app is in the mode the step
// is asking for, which makes them a reliable "you can act now" signal.
const SPLIT_TOOL_ACTIVE = '[data-tutorial="split-cancel"]';
const CUT_APPLY = '[data-tutorial="split-cut-apply"]';
const SATIN_SPLIT_ACTIVE = '[data-tutorial="satin-split-done"]';
const RAIL_SELECTION_ACTIVE = '[data-tutorial="satin-rail-selection-done"]';
const RAIL_PATHS = '[data-tutorial="satin-selectable-path"]';

/**
 * Where to cut, in the design's own coordinates.
 *
 * Read off a recorded session on this artwork (the recorder projects canvas
 * clicks back through the design's transform), so they land on the same places
 * on the emblem at any zoom, pan or window size. Each cut is a pair: start just
 * outside one edge of the band, finish just past the other.
 *
 * Every one of them sits where two bands run into each other. That is the whole
 * idea of the lesson — satin needs a band with two ends, and this emblem is
 * three bands fused into one shape.
 */
const CUTS_AT_THE_RING = [
  { x: 255, y: 329 }, { x: 405, y: 221 },   // wide oval, left of the ring
  { x: 1084, y: 246 }, { x: 1212, y: 328 }, // wide oval, right of the ring
  { x: 666, y: 890 }, { x: 826, y: 890 },   // tall oval, bottom of the ring
];

const CUTS_AT_THE_CROSSING = [
  { x: 581, y: 406 }, { x: 649, y: 411 },   // above left
  { x: 822, y: 411 }, { x: 902, y: 403 },   // above right
  { x: 559, y: 516 }, { x: 658, y: 524 },   // below left
  { x: 817, y: 531 }, { x: 927, y: 509 },   // below right
];

/** The ends of each freed band, for cutting it open into a satin column. */
const BAND_ENDS = [
  { x: 322, y: 302 }, { x: 1162, y: 306 },  // the wide oval, tip to tip
  { x: 613, y: 407 }, { x: 859, y: 410 },   // the crossing's upper piece
  { x: 602, y: 528 }, { x: 868, y: 528 },   // and its lower piece
];

/**
 * Cutting a fused shape apart, taught on the Car Emblem.
 *
 * Transcribed from a recorded session on this exact artwork. The design is one
 * pink layer holding three bands — a ring and two ovals — traced as a single
 * fused shape, and that is the point of the lesson: satin runs between two
 * edges along a band, so anywhere two bands meet has to be cut apart before any
 * of it can become satin. The copy reasons about these particular shapes, so
 * none of it transfers to another design.
 *
 * Steps the learner performs themselves advance on their own click rather than
 * a Next button, so the tutorial keeps pace with the real workflow.
 *
 * The shared prelude — welcome, design picker, processing — is prepended by the
 * provider, so this list starts from the point the Stitch Editor is open.
 */
const EMBLEM_STEPS = [
  LAYERS_INTRO_STEP,
  AUTO_FILL_BASELINE_STEP,

  {
    id: 'emb-select-layer-2',
    title: 'Pick the Layer to Stitch',
    description: `Click **layer ${layers.emblem}** — the **pink emblem**. A ring and two ovals, all sewn in one thread.`,
    target: `button[aria-label="Select layer ${layers.emblem}"]`,
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-layer-selected',
    actionRequired: true,
  },
  {
    id: 'emb-open-split-tool',
    title: 'One Shape, Three Bands',
    description: 'Satin runs **along a band, between its two edges** — so it needs a band with two ends. The trace gave us the ring and both ovals **fused into one shape**, with no ends anywhere.\n\nClick **Split** to cut them apart.',
    target: '[data-tutorial="stitch-tool-split"]',
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-split-tool',
    actionRequired: true,
  },
  {
    id: 'emb-cut-at-ring',
    title: 'Free the Ovals From the Ring',
    // One page: the markers are the instruction, and they only go up once the
    // text has been read to the end.
    description: 'Each cut is **two clicks** — just outside one edge, then just past the other. Cut where the ovals run into the ring: **left**, **right**, and at the **bottom**.',
    target: '[data-tutorial="stitch-canvas"]',
    position: 'center',
    highlight: true,
    noBackdrop: true,
    designPoints: CUTS_AT_THE_RING,
    clickPairs: true,
    showPointer: 'immediate',
    readyWhen: SPLIT_TOOL_ACTIVE,
    advanceOnTargetClick: true,
    requiredClicks: 6,
    waitForAction: 'emb-ring-cuts',
    actionRequired: true,
  },
  {
    id: 'emb-cut-at-crossing',
    title: 'Separate the Two Ovals',
    description: 'Now the four places the ovals cross each other — **above left and right**, then **below left and right**. Same two clicks each.',
    target: '[data-tutorial="stitch-canvas"]',
    position: 'center',
    highlight: true,
    noBackdrop: true,
    designPoints: CUTS_AT_THE_CROSSING,
    clickPairs: true,
    showPointer: 'immediate',
    readyWhen: SPLIT_TOOL_ACTIVE,
    advanceOnTargetClick: true,
    requiredClicks: 8,
    waitForAction: 'emb-crossing-cuts',
    actionRequired: true,
  },
  {
    id: 'emb-cut-apply',
    title: 'Make the Cuts',
    description: 'Click **Cut & Apply**. The one shape becomes separate pieces — a ring, and the ovals in bands.',
    target: CUT_APPLY,
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-cut-applied',
    actionRequired: true,
  },
  {
    id: 'emb-open-stitch-tool',
    title: 'Open the Stitch Settings',
    description: 'Click **Stitch** to choose how this layer is sewn.',
    target: '[data-tutorial="stitch-tool-stitch"]',
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-stitch-menu',
    actionRequired: true,
  },
  {
    id: 'emb-choose-satin',
    title: 'Choose Satin',
    description: '• **Fill** — solid stitching for large areas\n• **Satin** — smooth, glossy bands for borders and lettering\n• **Running** — a thin single line for fine detail\n\nEvery piece we just cut is a narrow band, so click **Satin**.',
    target: '[data-tutorial="stitch-type-satin"]',
    // All three are outlined while they're being described; the highlight
    // narrows to Satin on the page that asks for the click.
    pageTargets: [
      { multipleTargets: STITCH_TYPE_TARGETS },
      { target: '[data-tutorial="stitch-type-satin"]' },
    ],
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-satin-chosen',
    actionRequired: true,
  },
  {
    id: 'emb-replace-layer',
    title: 'Replace or Keep Both?',
    description: 'Click **Replace this layer** — we\'re rebuilding these pieces as satin from scratch, not stitching satin on top of what\'s there.',
    target: '[data-tutorial="stitch-switch-replace-layer"]',
    position: 'top',
    highlight: true,
    advanceOnTargetClick: true,
    // The prompt only appears when there's existing stitching to replace.
    skipIfMissing: 5000,
    waitForAction: 'emb-replace-confirmed',
    actionRequired: true,
  },
  {
    id: 'emb-select-satin-columns',
    title: 'The Ring Is Already a Band',
    description: 'The ring needs no cutting: it\'s a band with **two edges already drawn** — an outer circle and an inner one.\n\nClick **Select Satin Columns** to hand those two paths straight to satin.',
    target: '[data-tutorial="satin-method-select-satin-columns"]',
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-rail-mode',
    actionRequired: true,
  },
  {
    id: 'emb-pick-ring-edges',
    title: 'Click the Ring\'s Two Edges',
    description: 'Click the **outside** of the ring, then the **inside**. Satin fills the band between them.',
    target: '[data-tutorial="stitch-canvas"]',
    position: 'center',
    highlight: true,
    noBackdrop: true,
    // The ring's outer contour then its inner one — the first two paths in the
    // layer, before and after the cutting. Choosing columns is a path pick
    // rather than a free click, so the markers sit on the strokes themselves,
    // found by walking each path until a spot nothing is covering turns up.
    railPaths: [{ index: 0, at: 0.3 }, { index: 1, at: 0.3 }],
    showPointer: 'immediate',
    clickTarget: RAIL_PATHS,
    readyWhen: RAIL_SELECTION_ACTIVE,
    advanceOnTargetClick: true,
    requiredClicks: 2,
    waitForAction: 'emb-edges-picked',
    actionRequired: true,
  },
  {
    id: 'emb-rails-done',
    title: 'Finish the Ring',
    description: 'Click **Done**. The ring is satin.',
    target: RAIL_SELECTION_ACTIVE,
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-rails-done',
    actionRequired: true,
  },
  {
    id: 'emb-split-columns',
    title: 'The Ovals Need Cutting Open',
    description: 'The oval pieces are solid bands with no edges marked. **Split into Satin Columns** cuts a piece down its length, and the two halves become its edges.\n\nClick it to start.',
    target: '[data-tutorial="satin-method-split-into-satin-columns"]',
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-split-mode',
    actionRequired: true,
  },
  {
    id: 'emb-split-bands',
    title: 'Cut Each Band End to End',
    description: 'Two clicks per piece — **one end, then the other**. Cut the **long way** so the stitches lie across the band. The markers show you where.',
    target: '[data-tutorial="stitch-canvas"]',
    position: 'center',
    highlight: true,
    noBackdrop: true,
    designPoints: BAND_ENDS,
    clickPairs: true,
    showPointer: 'immediate',
    readyWhen: SATIN_SPLIT_ACTIVE,
    advanceOnTargetClick: true,
    requiredClicks: 6,
    waitForAction: 'emb-bands-split',
    actionRequired: true,
  },
  {
    id: 'emb-split-done',
    title: 'Finish the Columns',
    description: 'Click **Done**. Every piece of the emblem is satin now.',
    target: SATIN_SPLIT_ACTIVE,
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-split-finished',
    actionRequired: true,
  },

  CLOSE_STITCH_PANEL_STEP,
  SHOW_ALL_LAYERS_STEP,

  {
    id: 'emb-offset-intro',
    title: 'Why Layers Need Offsetting',
    description: 'Thread pulls the fabric, so shapes finish **narrower** than they look — leaving bare fabric where two colours meet. Growing the white slightly tucks it **under** the pink.',
    target: '[data-tutorial="stitch-tool-offset"]',
    position: 'bottom',
    showNext: true,
    highlight: true,
  },
  {
    id: 'emb-select-layer-1',
    title: 'Start With the Panel',
    description: `Click **layer ${layers.panel}** — the **white panel**.`,
    target: `button[aria-label="Select layer ${layers.panel}"]`,
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-layer-1',
    actionRequired: true,
  },
  {
    id: 'emb-open-offset',
    title: 'Open Offset',
    description: 'Click **Offset**.',
    target: '[data-tutorial="stitch-tool-offset"]',
    position: 'bottom',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-offset-open',
    actionRequired: true,
  },
  {
    // Setting the amount and applying it is one motion in one open panel, so
    // this can't be two steps: a learner who does the obvious thing would apply
    // before the tutorial asked, get no acknowledgement, then be told to apply
    // again — growing the layer twice. Offsets accumulate, so that quietly
    // damages the design. Advancing on the Apply click accepts either order.
    id: 'emb-offset-amount',
    title: 'Grow It Slightly',
    description: 'Drag the slider to about **0.6 mm** — enough to close the gap, small enough to keep the shape.\n\nThen click **Apply**.',
    target: '[data-tutorial="offset-apply"]',
    // Highlights the slider while the amount is explained, then the button that
    // commits it.
    pageTargets: [
      { target: '[data-tutorial="offset-amount"]' },
      { target: '[data-tutorial="offset-apply"]' },
    ],
    position: 'right',
    highlight: true,
    advanceOnTargetClick: true,
    waitForAction: 'emb-offset-applied',
    actionRequired: true,
  },

  ...createExportSteps({
    learned: [
      '**Cutting** a fused shape apart wherever bands meet',
      '**Satin** on both a ready-made band and one you cut open',
      '**Offsets** so neighbouring colours overlap instead of gapping',
      '**Export** at the right format and size',
    ],
  }),
];

export default EMBLEM_STEPS;
